
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mail, LoaderCircle } from "lucide-react";
import { formatDate } from "@/utils/formatters";
import type { PaymentDetails, SiteBooking } from "@/types/payment";

interface BookingDetailsSectionProps {
  paymentDetails: PaymentDetails | null;
  bookingDetails: SiteBooking[];
  loading: boolean;
  permitEmails?: Array<{id: string, email: boolean}>;
  resendingEmail: string | null;
  onResendEmail?: (permitId: string) => Promise<void>;
  recoveredBookingData?: any;
}

export const BookingDetailsSection = ({
  paymentDetails,
  bookingDetails,
  loading,
  permitEmails,
  resendingEmail,
  onResendEmail,
  recoveredBookingData
}: BookingDetailsSectionProps) => {
  // Return null if no booking details or data type is not rv-site
  if (
    (!paymentDetails?.itemType || paymentDetails.itemType !== 'rv-site') && 
    bookingDetails.length === 0 && 
    !recoveredBookingData
  ) {
    return null;
  }

  // Show loading state
  if (loading && bookingDetails.length === 0 && !recoveredBookingData) {
    return (
      <Card className="w-full p-4 my-4">
        <h3 className="text-lg font-medium mb-4">Booking Details</h3>
        <div className="space-y-2 mb-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </Card>
    );
  }
  
  // If we have recovered booking data but no actual booking details yet
  const showRecoveredData = recoveredBookingData && 
    bookingDetails.length === 0 && 
    recoveredBookingData.siteId && 
    recoveredBookingData.startDate && 
    recoveredBookingData.endDate;

  // Return nothing if still no data after loading
  if (bookingDetails.length === 0 && !showRecoveredData) {
    return null;
  }

  // Format dates for display
  const formatBookingDates = (startDate?: Date | string, endDate?: Date | string) => {
    if (!startDate || !endDate) return "Dates pending";
    
    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);
    
    return `${formattedStart} - ${formattedEnd}`;
  };

  // Calculate nights
  const calculateNights = (startDate?: Date | string, endDate?: Date | string) => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  
  // Determine if email has been sent for a booking
  const getEmailStatus = (bookingId: string) => {
    if (!permitEmails || permitEmails.length === 0) return false;
    const emailStatus = permitEmails.find(item => item.id === bookingId);
    return emailStatus ? emailStatus.email : false;
  };

  return (
    <Card className="w-full p-4 my-4">
      <h3 className="text-lg font-medium mb-4">RV Site Booking Details</h3>
      
      {bookingDetails.length > 0 ? (
        // Display actual booking details
        bookingDetails.map(booking => (
          <div key={booking.id} className="mb-4 pb-4 border-b border-gray-100 last:border-b-0 last:pb-0 last:mb-0">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-medium">{booking.site_details?.site_number || "RV Site"}</h4>
                <p className="text-sm text-gray-600">
                  Booking ID: {booking.booking_id || "Processing"}
                </p>
              </div>
              <Badge 
                className={`${booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                {booking.payment_status === 'paid' ? 'Confirmed' : 'Processing'}
              </Badge>
            </div>
            
            <div className="mt-3 text-sm text-gray-700">
              <p>
                <span className="font-medium">Dates:</span>{" "}
                {formatBookingDates(booking.start_date, booking.end_date)}
                {" "}
                ({calculateNights(booking.start_date, booking.end_date)} nights)
              </p>
              <p>
                <span className="font-medium">Location:</span>{" "}
                {booking.site_details?.location || ""}
              </p>
              
              {booking.booking_id && (
                <p className="mt-1">
                  <span className="font-medium">Email Status:</span>{" "}
                  <span className={getEmailStatus(booking.booking_id) ? "text-green-600" : "text-amber-600"}>
                    {getEmailStatus(booking.booking_id) ? "Confirmation sent" : "Sending confirmation..."}
                  </span>
                </p>
              )}
            </div>
            
            {booking.booking_id && onResendEmail && (
              <div className="mt-3 text-right">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onResendEmail(booking.booking_id)}
                  disabled={!booking.booking_id || resendingEmail === booking.booking_id}
                >
                  {resendingEmail === booking.booking_id ? (
                    <>
                      <LoaderCircle className="mr-2 h-3 w-3 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-3 w-3" />
                      {getEmailStatus(booking.booking_id) ? "Resend Email" : "Send Email"}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        ))
      ) : showRecoveredData ? (
        // Display recovered booking data while waiting for webhook processing
        <div className="mb-4">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium">{recoveredBookingData.siteName || "RV Site"}</h4>
              <p className="text-sm text-gray-600">
                Booking ID: Processing
              </p>
            </div>
            <Badge className="bg-amber-100 text-amber-800">
              Processing
            </Badge>
          </div>
          
          <div className="mt-3 text-sm text-gray-700">
            <p>
              <span className="font-medium">Dates:</span>{" "}
              {formatBookingDates(recoveredBookingData.startDate, recoveredBookingData.endDate)}
              {" "}
              ({calculateNights(recoveredBookingData.startDate, recoveredBookingData.endDate)} nights)
            </p>
          </div>
          
          <div className="mt-3 p-2 bg-blue-50 text-blue-700 text-xs rounded">
            Your booking is being processed. The site has been reserved for your selected dates.
          </div>
        </div>
      ) : null}
    </Card>
  );
};
